import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import BottomNav from '../../components/navigation/BottomNav';
import Header from '../../components/navigation/Header';

const UserDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, creditBalance, loading, refreshCredits } = useAuth();
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);

  useEffect(() => {
    if (location?.state?.paymentSuccess) {
      setShowPaymentPopup(true);
      // Clear the state so refreshing doesn't re-show the popup
      window.history?.replaceState({}, document.title);
    }
  }, [location?.state?.paymentSuccess]);

  const handleRefreshCredits = async () => {
    setIsRefreshingCredits(true);
    try {
      await refreshCredits?.();
    } finally {
      setIsRefreshingCredits(false);
    }
  };

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';

  const quickLinks = [
    { label: 'Upload Photos', path: '/photo-upload-interface', icon: 'Upload', description: 'Rank your photos with AI', color: 'from-purple-500 to-blue-500' },
    { label: 'Analysis History', path: '/dashboard-analysis-history', icon: 'History', description: 'View past rankings', color: 'from-blue-500 to-cyan-500' },
    { label: 'Purchase Credits', path: '/purchase-credits', icon: 'CreditCard', description: 'Buy more analysis credits', color: 'from-emerald-500 to-teal-500' },
    { label: 'AI Insights', path: '/gemini-profile-insights', icon: 'Sparkles', description: 'Get profile recommendations', color: 'from-orange-500 to-pink-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header
          user={user}
          onNavigate={(path) => navigate(path)}
          onPurchaseCredits={() => navigate('/purchase-credits')}
          onLogout={() => navigate('/login')}
        />
        <main className="max-w-4xl mx-auto px-4 py-8 pb-24">
          {/* Welcome Banner Skeleton */}
          <div className="bg-gradient-to-r from-purple-200 to-blue-200 rounded-2xl p-6 mb-8 animate-pulse">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-2">
                <div className="h-3 bg-white/50 rounded w-24"></div>
                <div className="h-7 bg-white/50 rounded w-40"></div>
                <div className="h-3 bg-white/50 rounded w-32"></div>
              </div>
              <div className="bg-white/30 rounded-xl px-6 py-4 text-center w-24">
                <div className="h-8 bg-white/50 rounded w-12 mx-auto mb-1"></div>
                <div className="h-3 bg-white/50 rounded w-14 mx-auto"></div>
              </div>
            </div>
          </div>

          {/* Stat Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3]?.map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-200"></div>
                  <div className="space-y-1.5">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-5 bg-gray-200 rounded w-12"></div>
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded w-28"></div>
              </div>
            ))}
          </div>

          {/* Quick Actions Skeleton */}
          <div className="h-5 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[1, 2, 3, 4]?.map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm flex items-center gap-4 animate-pulse">
                <div className="w-12 h-12 rounded-xl bg-gray-200 flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-28"></div>
                  <div className="h-3 bg-gray-100 rounded w-40"></div>
                </div>
                <div className="w-5 h-5 bg-gray-100 rounded ml-auto"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        user={user}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={() => navigate('/login')}
      />
      {/* Payment Success Popup */}
      {showPaymentPopup && (
        <div className="fixed top-4 right-4 z-50 max-w-[24%] min-w-[200px] bg-white border border-emerald-200 rounded-xl shadow-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="CheckCircle" size={18} color="#10b981" />
            </div>
            <p className="text-sm text-gray-700 leading-snug flex-1">
              Thank you for your payment! The credits are now added to your balance.
            </p>
            <button
              type="button"
              onClick={() => setShowPaymentPopup(false)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors ml-1"
              aria-label="Close"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 py-8 pb-24">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-purple-100 text-sm font-medium mb-1">Welcome back 👋</p>
              <h1 className="text-2xl font-bold">{displayName}</h1>
              <p className="text-purple-200 text-sm mt-1">{displayEmail}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-4 text-center">
              <div className="text-3xl font-bold">{creditBalance}</div>
              <div className="text-purple-100 text-sm font-medium">Credits</div>
            </div>
          </div>
        </div>

        {/* Credit Balance Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Icon name="Zap" size={20} color="#9333ea" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Credit Balance</p>
                <p className="text-2xl font-bold text-gray-900">{creditBalance}</p>
              </div>
            </div>
            {creditBalance === 0 && (
              <p className="text-xs text-orange-500 font-medium">No credits remaining</p>
            )}
            {creditBalance > 0 && creditBalance <= 2 && (
              <p className="text-xs text-amber-500 font-medium">Running low on credits</p>
            )}
            {creditBalance > 2 && (
              <p className="text-xs text-emerald-500 font-medium">Credits available</p>
            )}
            <button
              type="button"
              onClick={handleRefreshCredits}
              disabled={isRefreshingCredits}
              className="mt-3 text-xs text-blue-600 font-medium hover:underline disabled:opacity-60"
            >
              {isRefreshingCredits ? 'Refreshing...' : 'Refresh now'}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Icon name="User" size={20} color="#3b82f6" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Account</p>
                <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">{displayName}</p>
              </div>
            </div>
            <Link to="/settings" className="text-xs text-blue-500 font-medium hover:underline">Manage settings →</Link>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Icon name="ShieldCheck" size={20} color="#10b981" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Status</p>
                <p className="text-sm font-semibold text-gray-900">Active</p>
              </div>
            </div>
            <p className="text-xs text-emerald-500 font-medium">Account verified</p>
          </div>
        </div>

        {/* Quick Links */}
        <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {quickLinks?.map((link) => (
            <Link
              key={link?.path}
              to={link?.path}
              className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 flex items-center gap-4"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${link?.color} flex items-center justify-center flex-shrink-0`}>
                <Icon name={link?.icon} size={22} color="white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{link?.label}</p>
                <p className="text-sm text-gray-500">{link?.description}</p>
              </div>
              <Icon name="ChevronRight" size={18} color="#9ca3af" className="ml-auto" />
            </Link>
          ))}
        </div>

        {/* Credit CTA */}
        {creditBalance <= 2 && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Need more credits?</h3>
              <p className="text-sm text-gray-600">Purchase credits to unlock all 9 photo slots and premium features.</p>
            </div>
            <button
              onClick={() => navigate('/purchase-credits')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-600 transition-all shadow-md whitespace-nowrap"
            >
              Purchase Credits
            </button>
          </div>
        )}
      </main>

      {/* Hidden file input for camera button */}
      <input
        id="camera-file-input-dashboard"
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        multiple
      />

      <BottomNav activePath="/user-dashboard" cameraInputId="camera-file-input-dashboard" />
    </div>
  );
};

export default UserDashboard;
